import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  InputAdornment,
  OutlinedInput,
  Typography,
} from '@mui/material';
import ErrorMessage from '../ErrorMessage/ErrorMessage';

export interface Item<T = any> {
  value: T;
  name: string;
}

interface ObjectSelectProps<T = any> {
  value: Item<T> | null;
  onChange: (item: Item<T>) => void;
  items?: Item<T>[];
  getOptionKey: (value: T) => string | number;
  selectName: string;
  labelId: string;
  onBlur?: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  error?: boolean;
  errorMessage?: string | null;
  placeholder?: string | null;
}

export function ObjectSelect<T = any>({
  value,
  onChange,
  onBlur,
  items = [],
  getOptionKey,
  selectName,
  labelId,
  disabled = false,
  isLoading = false,
  error,
  errorMessage,
  placeholder,
}: ObjectSelectProps<T>): JSX.Element {
  const handleChange = (event: SelectChangeEvent<string>) => {
    const selectedKey = event.target.value;
    const selectedItem = items.find(
      (item) => String(getOptionKey(item.value)) === selectedKey
    );
    if (selectedItem) {
      onChange(selectedItem);
    }
  };

  return (
    <FormControl fullWidth disabled={disabled || isLoading} error={error}>
      <InputLabel id={labelId} shrink={placeholder ? true : undefined}>
        {selectName}
      </InputLabel>
      <Select
        labelId={labelId}
        value={value ? String(getOptionKey(value.value)) : ''}
        onChange={handleChange}
        onBlur={onBlur}
        disabled={disabled || isLoading}
        error={error}
        displayEmpty={!!placeholder}
        renderValue={
          placeholder
            ? (selected) => {
                if (!selected) {
                  return (
                    <Typography
                      component="span"
                      sx={{ color: 'text.secondary' }}
                    >
                      {placeholder}
                    </Typography>
                  );
                }
                const selectedItem = items.find(
                  (item) => String(getOptionKey(item.value)) === selected
                );
                return selectedItem?.name ?? '';
              }
            : undefined
        }
        input={
          <OutlinedInput
            sx={{
              '&.Mui-error:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'error.main',
              },
            }}
            endAdornment={
              isLoading ? (
                <InputAdornment position="end" sx={{ marginRight: '16px' }}>
                  <CircularProgress size={16} />
                </InputAdornment>
              ) : null
            }
          />
        }
      >
        {items.length === 0 && !isLoading ? (
          <MenuItem disabled value="">
            No data available
          </MenuItem>
        ) : (
          items.map((item) => {
            const key = String(getOptionKey(item.value));
            return (
              <MenuItem key={key} value={key}>
                {item.name}
              </MenuItem>
            );
          })
        )}
      </Select>
      {error && <ErrorMessage message={errorMessage} />}
    </FormControl>
  );
}
